import { useState, type ReactNode } from 'react'
import { ActionSheet, SheetMenu, type SheetMenuItem } from '../../components/ActionSheet'
import { Icon } from '../../components/Icon'
import type { LocalChannel } from '../../db/db'
import type { SheetRequest } from '../../hooks/gestures/useSheetTriggers'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { previewText, type UserMap } from '../../lib/channels'
import { MOBILE_QUERY } from '../../lib/platform'
import { toast } from '../../state/toasts'
import { useUi } from '../../state/ui'
import { useLeaveAccess } from '../channels/leave'
import { muteOptions, mutedUntilFor } from '../channels/muteOptions'
import { defaultNotify, muteEnds, quietState } from '../channels/quiet'
import { saveNotify } from '../channels/notify'
import { markChannelRead } from '../conversation/useReadTracking'

interface Props {
  channel: LocalChannel
  name: string
  meId: string
  users: UserMap
  /** The row's avatar, shown again at the top of the sheet on phones. */
  avatar: ReactNode
  request: SheetRequest
  onClose: () => void
}

/**
 * Quick actions for a conversation in the list (spec §3.6): mark as read, mute for a while or unmute, and leave where
 * allowed, with the same calls and rules as reading the conversation and its details.
 */
export function ConversationActions({ channel, name, meId, users, avatar, request, onClose }: Props) {
  const phone = useMediaQuery(MOBILE_QUERY)
  const [view, setView] = useState<'actions' | 'mute'>('actions')
  const leave = useLeaveAccess(channel, meId)

  const items: SheetMenuItem[] = []
  if (channel.unread > 0) {
    items.push({ id: 'read', label: 'Mark as read', icon: 'checkDouble', run: () => void markChannelRead(channel.id, channel.headSeq) })
  }
  const quiet = quietState(channel)
  if (quiet?.kind === 'off') {
    // A standing choice, so the way back is the conversation's usual setting rather than an end to a mute.
    items.push({
      id: 'notify-on',
      label: 'Turn notifications on',
      icon: 'bell',
      run: () => void saveNotify(channel, defaultNotify(channel), null).then((saved) => saved && toast(`Notifications on for ${name}`)),
    })
  } else if (quiet) {
    items.push({
      id: 'unmute',
      label: 'Unmute',
      icon: 'bell',
      detail: muteEnds(quiet.until) ? `until ${muteEnds(quiet.until)}` : undefined,
      run: () => void saveNotify(channel, channel.notify, null).then((saved) => saved && toast(`Unmuted ${name}`)),
    })
  } else {
    items.push({ id: 'mute', label: 'Mute', icon: 'mute', more: true, run: () => setView('mute') })
  }
  if (leave === 'allowed') {
    items.push({
      id: 'leave',
      label: channel.type === 'channel' ? 'Leave channel' : 'Leave group',
      icon: 'leave',
      danger: true,
      run: () => useUi.getState().confirmLeave(channel.id, name),
    })
  }

  const durations: SheetMenuItem[] = muteOptions.map((option) => ({
    id: option.id,
    label: option.label,
    run: () =>
      void saveNotify(channel, channel.notify, mutedUntilFor(option.id)).then(
        (saved) => saved && toast(`Muted ${name} ${option.ms === null ? 'until you unmute it' : `for ${option.label}`}`),
      ),
  }))

  return (
    <ActionSheet
      label="Conversation actions"
      request={request}
      onClose={onClose}
      view={view}
      // Back from the durations: focus returns to Mute.
      focusIn={(panel, previousView) => (previousView === 'mute' ? panel.querySelector<HTMLElement>('[data-item="mute"]') : null)}
    >
      {phone ? (
        <div className="sheet-conversation">
          {avatar}
          <div>
            <strong>{name}</strong>
            <p>{previewText(channel, users, meId)}</p>
          </div>
        </div>
      ) : null}

      {view === 'mute' ? (
        <div
          onKeyDown={(event) => {
            // ← and Escape go back to the actions, as Back does, rather than closing the sheet.
            if (event.key !== 'ArrowLeft' && event.key !== 'Escape') return
            event.preventDefault()
            event.stopPropagation()
            setView('actions')
          }}
        >
          <div className="sheet-heading">
            <button type="button" className="icon-button" aria-label="Back to conversation actions" onClick={() => setView('actions')}>
              <Icon name="back" />
            </button>
            <span>Mute for</span>
          </div>
          <SheetMenu label="Mute for" items={durations} onClose={onClose} />
        </div>
      ) : (
        <SheetMenu label="Actions" items={items} busy={leave === 'loading'} onClose={onClose} />
      )}
    </ActionSheet>
  )
}
